import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
// RecipientHistoryPopover.tsx -- per-recipient session history.
//
// Generalized from the old CoworkerHistoryPopover to handle any
// recipient kind: agent (kind:'coworker'), human peer (kind:'user'),
// default-FDN, or future group threads. Anchored to a button (📜) in
// the chat actions menu when a window has an active recipient.
//
// Powered by GET /api/p/:pid/chat/sessions/by-recipient/:recipientKey.
// The URL is a stable input contract for the popover but the server
// dispatches by recipient kind to the canonical index:
//
//   user-kind (1:1 DM)
//     -> by-pair index, keyed by lex-sorted(viewer, otherUser) joined
//        by '|'. Symmetric -- both parties find the same prefix. THE
//        canonical raindb pattern for bidirectional pair lookups.
//
//   coworker / fdn / group
//     -> by-recipient index, keyed by recipientKey (canonical for
//        those kinds because they're one-directional or self-sorted).
//
// O(conversations between viewer and recipient) reads -- single
// listKeys, no server-side filtering. Scales to messenger-class
// fanout because there's no scan of viewer's total inbox. See
// raindb-base foundation pack v1.1.0+ fdn-chat-sessions for the
// formation + schema contract.
//
// Resume flow: click a row -> openWindow with sessionId -> a NEW
// window opens with that session's history loaded. (Multi-window era:
// each row click opens its own window; no in-place "swap to this
// session" because the user might have other windows of value.)
//
// + New conversation: opens a fresh empty window with the same
// recipient. First message creates a new session anchored to them.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../utils/api.js';
import { useChatStore } from '../../hooks/useAgent.js';
import { recipientKey } from '../../utils/recipient.js';
function recipientDisplayName(r) {
    switch (r.kind) {
        case 'fdn': return 'FDN Assistant';
        case 'coworker': return r.name;
        case 'user': return r.name;
    }
}
export function RecipientHistoryPopover({ projectId, recipient, open, anchorRect, onClose, }) {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const [filter, setFilter] = useState('');
    const popRef = useRef(null);
    const key = recipientKey(recipient);
    const displayName = recipientDisplayName(recipient);
    // Re-fetch every time the popover is opened. The list is typically
    // small; doing it on each open means the most-recent session shows
    // up after the user finishes a turn and clicks history again.
    const refresh = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const r = await api.get(`/p/${projectId}/chat/sessions/by-recipient/${encodeURIComponent(key)}`);
            setSessions(r.sessions ?? []);
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : 'failed to load history');
        }
        finally {
            setLoading(false);
        }
    }, [projectId, key]);
    useEffect(() => {
        if (open) {
            void refresh();
            setFilter('');
        }
    }, [open, refresh]);
    // Close on outside click + Escape.
    useEffect(() => {
        if (!open)
            return;
        const onDocClick = (e) => {
            if (popRef.current && !popRef.current.contains(e.target))
                onClose();
        };
        const onKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onClose]);
    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q)
            return sessions;
        return sessions.filter((s) => {
            const t = (s.title ?? '').toLowerCase();
            const p = (s.lastMessagePreview ?? '').toLowerCase();
            return t.includes(q) || p.includes(q);
        });
    }, [sessions, filter]);
    const onResume = useCallback((sid) => {
        useChatStore.getState().openWindow({ recipient, sessionId: sid });
        onClose();
    }, [recipient, onClose]);
    const onNewConversation = useCallback(() => {
        useChatStore.getState().openWindow({ recipient });
        onClose();
    }, [recipient, onClose]);
    if (!open || !anchorRect)
        return null;
    // Position: 8px below the anchor's bottom edge, flush-right to it.
    // Width 360, max-height 420 with internal scroll. Clamped to
    // viewport to avoid going off-screen.
    const POP_W = 360;
    const left = Math.min(Math.max(8, anchorRect.x + anchorRect.width - POP_W), window.innerWidth - POP_W - 8);
    const top = Math.min(anchorRect.y + anchorRect.height + 6, window.innerHeight - 200);
    return (_jsxs("div", { ref: popRef, className: "recipient-history-popover", style: { left, top, width: POP_W }, role: "dialog", "aria-label": `Conversation history with ${displayName}`, children: [_jsxs("div", { className: "recipient-history-header", children: [_jsxs("span", { className: "recipient-history-title", children: ["History ", displayName ? `with ${displayName}` : ''] }), _jsx("button", { type: "button", className: "ghost", onClick: onClose, title: "Close", children: '\u2715' })] }), _jsx("div", { className: "recipient-history-search", children: _jsx("input", { autoFocus: true, value: filter, onChange: (e) => setFilter(e.target.value), placeholder: 'Filter by title or content\u2026' }) }), _jsxs("div", { className: "recipient-history-body", children: [loading && _jsx("div", { className: "recipient-history-empty", children: 'Loading\u2026' }), err && _jsx("div", { className: "error", style: { margin: 12 }, children: err }), !loading && !err && sessions.length === 0 && (_jsxs("div", { className: "recipient-history-empty", children: [_jsx("p", { children: "No past conversations yet." }), _jsxs("p", { style: { color: 'var(--fg-muted)', fontSize: 12 }, children: ["Send a message to start your first chat with ", displayName, "."] })] })), !loading && !err && sessions.length > 0 && filtered.length === 0 && (_jsx("div", { className: "recipient-history-empty", children: _jsxs("p", { style: { color: 'var(--fg-muted)' }, children: ["No matches for \"", filter, "\"."] }) })), !loading && filtered.map((s) => (_jsxs("button", { type: "button", className: "recipient-history-row", onClick: () => onResume(s.sessionId), title: `Open "${s.title ?? 'Untitled'}" in a new window`, children: [_jsx("div", { className: "recipient-history-row-title", children: s.title ?? 'Untitled conversation' }), _jsxs("div", { className: "recipient-history-row-meta", children: [formatRelative(s.lastMessageAt), typeof s.messageCount === 'number' && s.messageCount > 0 && (_jsxs("span", { children: [" ", '\u00b7', " ", s.messageCount, " message", s.messageCount === 1 ? '' : 's'] }))] }), s.lastMessagePreview && (_jsx("div", { className: "recipient-history-row-preview", children: s.lastMessagePreview.slice(0, 140) }))] }, s.sessionId)))] }), _jsx("div", { className: "recipient-history-footer", children: _jsx("button", { type: "button", onClick: onNewConversation, children: '+ New conversation' }) })] }));
}
/** Format a timestamp as a friendly relative string. */
function formatRelative(t) {
    if (!t)
        return '—';
    const dMs = Date.now() - new Date(t).getTime();
    if (Number.isNaN(dMs))
        return '—';
    const s = Math.floor(dMs / 1000);
    if (s < 60)
        return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60)
        return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24)
        return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30)
        return `${d}d ago`;
    return new Date(t).toLocaleDateString();
}
